/** Opções alinhadas a `KanbanItem`, `KanbanItemsController#change_status` e automações do funil. */

export const WORKFLOW_KANBAN_ITEM_STATUS_OPTIONS = [
  { value: 'open', label: 'Aberto' },
  { value: 'won', label: 'Ganho' },
  { value: 'lost', label: 'Perdido' },
];

const KANBAN_STATUS_VALUES = new Set(
  WORKFLOW_KANBAN_ITEM_STATUS_OPTIONS.map(o => o.value)
);

export const normalizeWorkflowKanbanItemStatus = raw => {
  const s = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  return KANBAN_STATUS_VALUES.has(s) ? s : null;
};

export const workflowKanbanItemStatusLabel = value =>
  WORKFLOW_KANBAN_ITEM_STATUS_OPTIONS.find(o => o.value === value)?.label ||
  (value ? String(value) : '');

export const normalizeWorkflowKanbanFunnelId = raw => {
  const n =
    raw === null || raw === undefined || raw === '' ? NaN : Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** Chave de etapa no funil (string não vazia). */
export const normalizeWorkflowKanbanFunnelStageKey = raw => {
  const s = typeof raw === 'string' ? raw.trim() : '';
  return s.length ? s : '';
};

export const normalizeKanbanWorkflowTriggerFields = (kind, d = {}) => {
  const kanban_funnel_id = normalizeWorkflowKanbanFunnelId(d.kanban_funnel_id);
  const kanban_to_stage = normalizeWorkflowKanbanFunnelStageKey(d.kanban_to_stage);
  const kanban_from_stage = normalizeWorkflowKanbanFunnelStageKey(
    d.kanban_from_stage
  );
  return {
    kanban_funnel_id,
    kanban_to_stage:
      kind === 'kanban_item_stage_changed' ? kanban_to_stage : '',
    kanban_from_stage:
      kind === 'kanban_item_stage_changed' ? kanban_from_stage : '',
  };
};

export const normalizeKanbanCreateItemFields = d => ({
  kanban_funnel_id: normalizeWorkflowKanbanFunnelId(d.kanban_funnel_id),
  kanban_funnel_stage: normalizeWorkflowKanbanFunnelStageKey(
    d.kanban_funnel_stage
  ),
  kanban_item_title:
    typeof d.kanban_item_title === 'string' ? d.kanban_item_title : '',
  kanban_item_description:
    typeof d.kanban_item_description === 'string'
      ? d.kanban_item_description
      : '',
  kanban_offer_id: normalizeWorkflowKanbanFunnelId(d.kanban_offer_id),
  kanban_schedule_date:
    typeof d.kanban_schedule_date === 'string' ? d.kanban_schedule_date : '',
  kanban_schedule_time:
    typeof d.kanban_schedule_time === 'string' ? d.kanban_schedule_time : '',
  kanban_note_text:
    typeof d.kanban_note_text === 'string' ? d.kanban_note_text : '',
});

export const normalizeKanbanMoveToStageFields = d => ({
  kanban_funnel_id: normalizeWorkflowKanbanFunnelId(d.kanban_funnel_id),
  kanban_funnel_stage: normalizeWorkflowKanbanFunnelStageKey(
    d.kanban_funnel_stage
  ),
});

export const normalizeKanbanAssignAgentFields = d => {
  const aid = d.kanban_agent_id;
  const n =
    aid === null || aid === undefined || aid === '' ? NaN : Number(aid);
  return {
    kanban_agent_id: Number.isFinite(n) ? n : null,
  };
};

export const normalizeKanbanRemoveAgentFields = d => {
  const aid = d.kanban_remove_agent_id;
  const n =
    aid === null || aid === undefined || aid === '' ? NaN : Number(aid);
  return {
    kanban_remove_agent_id: Number.isFinite(n) ? n : null,
  };
};

export const normalizeKanbanAddNoteFields = d => ({
  kanban_note_text:
    typeof d.kanban_note_text === 'string' ? d.kanban_note_text : '',
});

export const normalizeKanbanChangeItemStatusFields = d => ({
  kanban_item_status: normalizeWorkflowKanbanItemStatus(d.kanban_item_status),
});

/** Resumo no cartão — gatilhos Kanban */
export const summarizeKanbanWorkflowTrigger = flowNode => {
  if (!flowNode || flowNode.type !== 'trigger') return '';
  const kind = flowNode.data?.kind || '';
  if (
    ![
      'kanban_item_created',
      'kanban_item_updated',
      'kanban_item_stage_changed',
      'kanban_item_deleted',
    ].includes(kind)
  ) {
    return '';
  }
  const fid = normalizeWorkflowKanbanFunnelId(flowNode.data?.kanban_funnel_id);
  const parts = [];
  if (fid) parts.push(`Funil #${fid}`);
  if (kind === 'kanban_item_stage_changed') {
    const fromS = normalizeWorkflowKanbanFunnelStageKey(
      flowNode.data?.kanban_from_stage
    );
    const toS = normalizeWorkflowKanbanFunnelStageKey(
      flowNode.data?.kanban_to_stage
    );
    if (fromS) parts.push(`de «${fromS}»`);
    if (toS) parts.push(`→ «${toS}»`);
  }
  return parts.length ? parts.join(' · ') : 'Qualquer funil';
};

/** Resumo no cartão — ações Kanban */
export const summarizeKanbanWorkflowAction = (
  flowNode,
  funnelNameById,
  agentNameById
) => {
  if (!flowNode || flowNode.type !== 'action') return '';
  const kind = flowNode.data?.kind || '';
  const fname = id => {
    if (!id) return '';
    const name = funnelNameById?.(id);
    return name || `#${id}`;
  };
  const aname = id => {
    if (!id) return '';
    const name = agentNameById?.(id);
    return name || `#${id}`;
  };

  switch (kind) {
    case 'kanban_create_item': {
      const { kanban_funnel_id, kanban_funnel_stage } =
        normalizeKanbanCreateItemFields(flowNode.data || {});
      const fs = kanban_funnel_stage || '…';
      if (!kanban_funnel_id) return `Criar cartão · ${fs}`;
      return `Criar · ${fname(kanban_funnel_id)} · ${fs}`;
    }
    case 'kanban_move_to_stage': {
      const { kanban_funnel_id, kanban_funnel_stage } =
        normalizeKanbanMoveToStageFields(flowNode.data || {});
      const fs = kanban_funnel_stage || '…';
      if (!kanban_funnel_id) return `Mover · ${fs}`;
      return `Mover · ${fname(kanban_funnel_id)} · ${fs}`;
    }
    case 'kanban_assign_agent': {
      const { kanban_agent_id } = normalizeKanbanAssignAgentFields(
        flowNode.data || {}
      );
      return kanban_agent_id
        ? `Atribuir ${aname(kanban_agent_id)}`
        : 'Atribuir agente…';
    }
    case 'kanban_remove_agent': {
      const { kanban_remove_agent_id } = normalizeKanbanRemoveAgentFields(
        flowNode.data || {}
      );
      return kanban_remove_agent_id
        ? `Remover ${aname(kanban_remove_agent_id)}`
        : 'Remover agente…';
    }
    case 'kanban_add_note': {
      const { kanban_note_text } = normalizeKanbanAddNoteFields(
        flowNode.data || {}
      );
      const t = String(kanban_note_text || '').trim();
      if (!t) return 'Nota no cartão…';
      return t.length > 40 ? `${t.slice(0, 40)}…` : t;
    }
    case 'kanban_change_item_status': {
      const { kanban_item_status } = normalizeKanbanChangeItemStatusFields(
        flowNode.data || {}
      );
      return kanban_item_status
        ? `Status: ${workflowKanbanItemStatusLabel(kanban_item_status)}`
        : 'Status do cartão…';
    }
    default:
      return '';
  }
};
